import { Avatar, Modal, Paper, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { InfinityPagination } from "../hooks/Pagination";
import { CandidateSearch, CandidateTable } from "../interfaces/Candidate";
import { ManagerSearch, ManagerTable } from "../interfaces/Manager";
import { Status } from "../interfaces/Status";
import { TaskTable } from "../interfaces/Task";
import {
	GetCandidates,
	GetManagers,
	GetStatuses,
	UpdateTask,
} from "../requester/functions";
import { MAIN_URL } from "../requester/requester";
import { getFullName } from "../utilities/string";
import { VK } from "../utilities/validate";
import CommentBox from "./CommentBox";
import DataDateTime from "./DataInputs/DataDateTime";
import DataMultiSelect from "./DataInputs/DataMultiSelect";
import EditMenu from "./EditMenu";
import EditText from "./EditText";

interface IProps {
	open: boolean;
	onClose(): void;
	onUpdate(id: number): void;
	task?: TaskTable;
	userId?: number;
}

const UpdateTaskModal = (props: IProps) => {
	const theme = useTheme();

	const [task, setTask] = useState<TaskTable>();
	const [status, setStatus] = useState<Status[]>([]);
	const [urgency, setUrgency] = useState<Status[]>([]);
	const [managers, setManagers] = useState<ManagerTable[]>([]);
	const [watchers, setWatchers] = useState<ManagerTable[]>([]);
	const [candidates, setCandidates] = useState<CandidateTable[]>([]);

	const [searchCandidate, setSearchCandidate] = useState<CandidateSearch>({});
	const [searchManager, setSearchManager] = useState<ManagerSearch>({});
	const [searchWatcher, setSearchWatcher] = useState<ManagerSearch>({});

	const renderCandidate = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`candidate-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setTaskCandidate(item.id, rowId);
			}}
		>
			<Avatar />
			<Typography>
				{item.last_name} {item.first_name}
			</Typography>
		</div>
	);

	const renderManager = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`manager-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setTaskManager(item.id, rowId);
			}}
		>
			<Avatar src={`${MAIN_URL}manager/avatar/${item.avatar}`} />
			<Typography>
				{item.last_name} {item.first_name}
			</Typography>
		</div>
	);

	// const renderWatcher = (
	// 	item: any,
	// 	index: number,
	// 	selectedId: number,
	// 	rowId: number
	// ) => (
	// 	<div
	// 		key={`watcher-${index}`}
	// 		className="flex items-center w-full gap-3 rounded-lg px-1 py-1"
	// 		style={
	// 			selectedId === item.id
	// 				? { border: "solid 2px", borderColor: theme.palette.primary.main }
	// 				: {}
	// 		}
	// 		onClick={() => {
	// 			setTaskWatcher(item.id, rowId);
	// 		}}
	// 	>
	// 		<Avatar src={`${MAIN_URL}manager/avatar/${item.avatar}`} />
	// 		<Typography>
	// 			{item.last_name} {item.first_name}
	// 		</Typography>
	// 	</div>
	// );

	const renderStatus = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`status-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setTaskStatus(item.id, rowId);
			}}
		>
			<Typography>{item.name}</Typography>
		</div>
	);

	const renderUrgency = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`urgency-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setTaskUrgency(item.id, rowId);
			}}
		>
			<Typography>{item.name}</Typography>
		</div>
	);

	const setTaskName = (name: string, task_id: number) => {
		UpdateTask({ name, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskDescription = (description: string, task_id: number) => {
		UpdateTask({ description, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskDeadline = (deadline: number, task_id: number) => {
		UpdateTask({ deadline, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskManager = (id: number, task_id: number) => {
		UpdateTask({ id_manager: id, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskWatchers = (ids: number[], task_id: number) => {
		UpdateTask({ watchers: ids, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskCandidate = (id: number, task_id: number) => {
		UpdateTask({ id_candidate: id, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskUrgency = (id: number, task_id: number) => {
		UpdateTask({ id_urgency: id, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	const setTaskStatus = (id: number, task_id: number) => {
		UpdateTask({ id_status: id, id: [task_id] }, (resp) => {
			props.onUpdate(task_id);
		});
	};

	useEffect(() => {
		GetCandidates(
			{
				search_by: searchCandidate,
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setCandidates(resp.data.data);
				}
			}
		);
	}, [searchCandidate]);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchManager,
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setManagers(resp.data.data);
				}
			}
		);
	}, [searchManager]);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchWatcher,
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setWatchers(resp.data.data);
				}
			}
		);
	}, [searchWatcher]);

	useEffect(() => {
		setTask(props.task);
	}, [props.task]);

	useEffect(() => {
		GetStatuses({ filter_by: { type: "urgency" } }, (resp) => {
			if (resp.status === "ok" && resp.data) {
				setUrgency(resp.data);
			}
		});
		GetStatuses({ filter_by: { type: "task" } }, (resp) => {
			if (resp.status === "ok" && resp.data) {
				setStatus(resp.data);
			}
		});
	}, []);

	return props.task !== undefined ? (
		<Modal
			open={props.open}
			onClose={props.onClose}
			className="flex items-center flex-col justify-center overflow-auto"
		>
			<Paper className="focus-visible:outline-none p-5 max-w-[1080px] min-w-[600px] flex justify-between gap-3">
				<div className="flex flex-col gap-2">
					<div>
						<EditText
							value={task?.name || ""}
							onSetValue={(value) => {
								setTaskName(value, task?.id ?? 0);
							}}
							inputProps={{
								required: true,
								label: "Название",
								error: !VK(task?.name),
							}}
						/>
					</div>

					<div>
						<EditText
							value={task?.description || ""}
							onSetValue={(value) => {
								setTaskDescription(value, task?.id ?? 0);
							}}
							inputProps={{
								required: true,
								label: "Описание",
								error: !VK(task?.description),
							}}
						/>
					</div>

					<div className="py-2">
						<DataDateTime
							label="Срок"
							value={task}
							keyValue="deadline"
							updateValue={(value) => {
								setTaskDeadline(value, task?.id ?? 0);
							}}
						/>
					</div>

					<div>
						<Typography variant="caption">Статус</Typography>
						<EditMenu
							renderElement={renderStatus}
							data={status}
							selectedId={task?.status.id}
							item={task}
							hideSearch
						>
							{task?.status.name || "Нет"}
						</EditMenu>
					</div>

					<div>
						<Typography variant="caption">Срочность</Typography>
						<EditMenu
							renderElement={renderUrgency}
							data={urgency}
							selectedId={task?.urgency?.id || 0}
							item={task}
							hideSearch
						>
							{task?.urgency?.name || "Нет"}
						</EditMenu>
					</div>

					<div>
						<Typography variant="caption">Кандидат</Typography>
						<EditMenu
							renderElement={renderCandidate}
							data={candidates}
							search={searchCandidate.full_name}
							selectedId={task?.candidate.id}
							item={task}
							setSearch={function (value: string) {
								setSearchCandidate((s) => {
									s.full_name = value;
									return { ...s };
								});
							}}
						>
							{task?.candidate.full_name || "Нет"}
						</EditMenu>
					</div>

					<div>
						<Typography variant="caption">Отвественный</Typography>
						<EditMenu
							renderElement={renderManager}
							data={managers}
							search={searchManager.full_name}
							selectedId={task?.manager.id}
							item={task}
							setSearch={function (value: string) {
								setSearchManager((s) => {
									s.full_name = value;
									return { ...s };
								});
							}}
						>
							{task?.manager.full_name || "Нет"}
						</EditMenu>
					</div>

					<div>
						<DataMultiSelect
							label="Наблюдатели"
							list={watchers}
							objects
							value={task}
							keyValue={"watchers"}
							setValue={(item: any) =>
								setTaskWatchers(item.watchers, task?.id ?? 0)
							}
							renderItemTitle={(item: ManagerTable) =>
								getFullName(item.last_name, item.first_name, item.patronymic)
							}
						/>
					</div>
				</div>
				<div className="-m-5 ml-0">
					<CommentBox type="task" id_obj={props.task?.id ?? 0} />
				</div>
			</Paper>
		</Modal>
	) : null;
};

export default UpdateTaskModal;
